// admin/ManageUsers.jsx
// View all registered users and their roles.
// Admin can promote a user to admin or demote an admin back to user.

import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase/config";
import { formatDate } from "../../utils/helpers";
import { useAuth } from "../../context/AuthContext";

export default function ManageUsers() {
  const { currentUser } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [updating, setUpdating] = useState(null); // uid being updated
  const [confirmUser, setConfirmUser] = useState(null); // user to confirm role change
  const [confirmStatusUser, setConfirmStatusUser] = useState(null); // user to confirm enable/disable status

  async function fetchUsers() {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "users"), orderBy("createdAt", "desc")));
    setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { fetchUsers(); }, []);

  // Toggle role between 'admin' and 'user'
  async function handleRoleToggle(uid, currentRole) {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      setUpdating(uid);
      await updateDoc(doc(db, "users", uid), { role: newRole });
      setUsers((prev) =>
        prev.map((u) => (u.uid === uid ? { ...u, role: newRole } : u))
      );
    } catch (err) {
      console.error("Role update failed:", err);
    } finally {
      setUpdating(null);
    }
  }

  // Toggle user status between 'active' and 'disabled'
  async function handleStatusToggle(uid, currentlyDisabled) {
    const newStatus = !currentlyDisabled;
    try {
      setUpdating(uid);
      await updateDoc(doc(db, "users", uid), { isDisabled: newStatus });
      setUsers((prev) =>
        prev.map((u) => (u.uid === uid ? { ...u, isDisabled: newStatus } : u))
      );
    } catch (err) {
      console.error("Status update failed:", err);
    } finally {
      setUpdating(null);
    }
  }

  const filtered = users.filter(
    (u) =>
      !search ||
      (u.displayName || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(search.toLowerCase())
  );

  // Counts
  const adminCount = users.filter((u) => u.role === "admin").length;
  const userCount  = users.filter((u) => u.role !== "admin").length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500">
            {users.length} total · {adminCount} admin{adminCount !== 1 ? "s" : ""} · {userCount} customer{userCount !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Search */}
      <input
        type="text" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or email…"
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-72"
      />

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 animate-pulse">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3">User</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Joined</th>
                  <th className="text-left px-5 py-3">Role</th>
                  <th className="text-center px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((user) => {
                  const isCurrentUser = user.uid === currentUser?.uid;
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      {/* Avatar + Name */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                            user.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"
                          }`}>
                            {(user.displayName || user.email || "?")[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">
                              {user.displayName || "—"}
                              {isCurrentUser && (
                                <span className="ml-2 text-xs text-gray-400">(you)</span>
                              )}
                            </p>
                            {user.phone && <p className="text-xs text-gray-400">{user.phone}</p>}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-3 text-gray-600">{user.email}</td>

                      <td className="px-5 py-3 text-gray-500">{formatDate(user.createdAt)}</td>

                      {/* Role badge */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`capitalize text-xs font-semibold px-2.5 py-1 rounded-full ${
                            user.role === "admin"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-gray-100 text-gray-600"
                          }`}>
                            {user.role || "user"}
                          </span>
                          {user.isDisabled && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 bg-red-100 px-2 py-0.5 rounded-md">
                              Disabled
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Toggle role button */}
                      <td className="px-5 py-3 text-center">
                        {isCurrentUser ? (
                          // Cannot demote yourself
                          <span className="text-xs text-gray-400 italic">Can't edit own account</span>
                        ) : (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => setConfirmUser(user)}
                              disabled={updating === user.uid}
                              className={`text-xs px-3 py-1 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                                user.role === "admin"
                                  ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  : "bg-purple-50 text-purple-700 hover:bg-purple-100"
                              }`}
                            >
                              {updating === user.uid
                                ? "Updating…"
                                : user.role === "admin"
                                ? "Demote"
                                : "Promote Admin"}
                            </button>
                            <button
                              onClick={() => setConfirmStatusUser(user)}
                              disabled={updating === user.uid}
                              className={`text-xs px-3 py-1 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                                user.isDisabled
                                  ? "bg-green-50 text-green-700 hover:bg-green-100"
                                  : "bg-red-50 text-red-600 hover:bg-red-100"
                              }`}
                            >
                              {user.isDisabled ? "Enable" : "Disable"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info note */}
      <p className="text-xs text-gray-400">
        💡 Role changes take effect the next time the user logs in. Admins get access to this admin dashboard.
      </p>

      {/* Status Confirmation Modal */}
      {confirmStatusUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <p className="text-4xl mb-3">{confirmStatusUser.isDisabled ? "✅" : "🚫"}</p>
            <h3 className="font-bold text-gray-900 mb-2">
              {confirmStatusUser.isDisabled ? "Enable User?" : "Disable User?"}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to {confirmStatusUser.isDisabled ? "enable" : "disable"} access for <strong>{confirmStatusUser.displayName || confirmStatusUser.email}</strong>?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmStatusUser(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button 
                onClick={() => {
                  handleStatusToggle(confirmStatusUser.uid, confirmStatusUser.isDisabled);
                  setConfirmStatusUser(null);
                }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white ${
                  confirmStatusUser.isDisabled ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                }`}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <p className="text-4xl mb-3">⚠️</p>
            <h3 className="font-bold text-gray-900 mb-2">
              {confirmUser.role === "admin" ? "Demote to User?" : "Promote to Admin?"}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to change the role of <strong>{confirmUser.displayName || confirmUser.email}</strong>?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmUser(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button 
                onClick={() => {
                  handleRoleToggle(confirmUser.uid, confirmUser.role || "user");
                  setConfirmUser(null);
                }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white ${
                  confirmUser.role === "admin" ? "bg-gray-900 hover:bg-gray-800" : "bg-purple-600 hover:bg-purple-700"
                }`}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
